/**
 * Stage-2 heuristic port of `extractDetail` + `sanitizeKey` from
 * `src/legacy/utils/errorTypeHandler.ts`. Used only as a fallback when the new backend
 * shape (`schema.ts`) fails to parse — see `resolveApiError.ts`.
 */

/**
 * Strips i18next interpolation/plural suffixes (`key:context`, `key[count]`) so a raw
 * legacy detail string can be matched against the contract whitelist. Applied in stage 2
 * only — stage 1 contract keys must match exactly, sanitizing them would hide drift.
 */
export const sanitizeKey = (key: string): string => {
	if (!key) return '';
	const left = key.split(':')[0] ?? '';
	return (left.split('[')[0] ?? '').trim();
};

/**
 * Best-effort parse of a string that looks like a serialized object. Tries real JSON
 * first, then falls back to swapping Python's single-quote dict repr (`{'k': 'v'}`) for
 * double quotes — some backend error paths stringify a dict instead of nesting it as
 * real JSON. Never throws; returns undefined on any failure.
 */
const tryParseObjectLiteral = (raw: string): Record<string, unknown> | undefined => {
	for (const candidate of [raw, raw.replace(/'/g, '"')]) {
		try {
			const parsed: unknown = JSON.parse(candidate);
			if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
				return parsed as Record<string, unknown>;
			}
		} catch {
			// try the next candidate / fall through to undefined
		}
	}
	return undefined;
};

/**
 * Best-effort extraction of a candidate detail/key from an arbitrary legacy-shaped
 * response body: `detail ?? error ?? message ?? code`, unwrapping the first string found
 * in an array, or `.code`/`.message` from a nested object. If the extracted string itself
 * looks like a serialized object (a backend sometimes double-wraps this way), recurses
 * one level into it using the same priority order.
 */
export const extractLegacyDetail = (data: unknown): string | undefined => {
	if (!data || typeof data !== 'object') return undefined;

	const record = data as Record<string, unknown>;
	const candidate = record['detail'] ?? record['error'] ?? record['message'] ?? record['code'];

	if (typeof candidate === 'string') {
		const trimmed = candidate.trim();
		if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
			const nested = tryParseObjectLiteral(trimmed);
			const nestedResult = nested && extractLegacyDetail(nested);
			if (nestedResult) return nestedResult;
		}
		return candidate;
	}

	if (Array.isArray(candidate)) {
		const first = candidate.find((item): item is string => typeof item === 'string');
		if (first) return first;
	}

	if (typeof candidate === 'object' && candidate !== null) {
		const nested = candidate as Record<string, unknown>;
		if (typeof nested['code'] === 'string') return nested['code'];
		if (typeof nested['message'] === 'string') return nested['message'];
	}

	return undefined;
};
